/**
 * PATH: apps/quote/src/components/builder/panels/KPISummaryPanel.tsx
 * FILE_ID: QB-KPI-SUMMARY-PANEL-001
 * PURPOSE: Right-rail summary card showing the per-discipline margin
 *          breakdown (Labour / Products / Plant / Subcontractors /
 *          Other Charges) sourced from Quote.config.kpiSummary.
 * WHY: Estimators reviewing the demo quote in BRAVADA-DEMO-21 want
 *      the same KPI table Excel renders on the "KPI Summary" tab —
 *      it's the fastest read on whether the quote is profitable.
 *
 * FLOW:
 *   QuoteDetail.config → parseKpiSummary() → render rows + total
 *
 * DEPENDENCIES:
 *   - kpi-summary-parser: pure parse + margin helpers
 *   - QuoteDetail.config: optional JSON blob from the backend
 *
 * RELATED:
 *   - QB-KPI-SUMMARY-PARSER-001 — pure parser
 *   - QB-BRAVADA-DEMO-MAP-001 (BE) populates config.kpiSummary
 */

import { formatCurrency } from '@/lib/utils'
import type { QuoteDetail } from '@/lib/quoteApi'
import { Briefcase, Hammer, Package, Truck, Users, Sigma, BarChart3, Clock, Sparkles, TrendingUp } from 'lucide-react'
import { parseKpiSummary, marginPercent, type KPIRow, type AdjustedKPI } from './kpi-summary-parser'

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

const ROW_ICONS: Record<string, typeof Briefcase> = {
  Labour: Hammer,
  Products: Package,
  Plant: Truck,
  Subcontractors: Users,
  'Other Charges': Briefcase,
}

function marginColour(pct: number): string {
  if (pct >= 30) return 'text-emerald-600 dark:text-emerald-400'
  if (pct >= 15) return 'text-amber-600 dark:text-amber-400'
  return 'text-rose-600 dark:text-rose-400'
}

function KPIRowItem({ row, totalChargeout }: { row: KPIRow; totalChargeout: number }) {
  const margin = row.chargeout - row.cost
  const pct = marginPercent(row.cost, row.chargeout)
  const sharePct = totalChargeout > 0 ? (row.chargeout / totalChargeout) * 100 : 0
  const Icon = ROW_ICONS[row.label] || Briefcase
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{row.label}</span>
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
          {sharePct.toFixed(1)}% of quote
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-slate-500 dark:text-slate-400 mb-0.5">Cost</div>
          <div className="font-medium text-slate-700 dark:text-slate-200 tabular-nums">
            {formatCurrency(row.cost)}
          </div>
        </div>
        <div>
          <div className="text-slate-500 dark:text-slate-400 mb-0.5">Charge-out</div>
          <div className="font-medium text-slate-700 dark:text-slate-200 tabular-nums">
            {formatCurrency(row.chargeout)}
          </div>
        </div>
        <div>
          <div className="text-slate-500 dark:text-slate-400 mb-0.5">Margin</div>
          <div className={'font-semibold tabular-nums ' + marginColour(pct)}>
            {formatCurrency(margin)} ({pct.toFixed(1)}%)
          </div>
        </div>
      </div>
    </div>
  )
}

function AdjustedGmBlock({ adjusted }: { adjusted: AdjustedKPI }) {
  // Bravada's "directors' decision number". The raw GM (top card) is
  // what the costing model says the quote should sell for. The adjusted
  // GM is what the contract ACTUALLY sells for after rounding +
  // mobilisation/night-shift premiums get loaded on top. Side-by-side
  // comparison is how the estimator decides whether to push back on
  // the contract value or accept the margin creep.
  const creepColour =
    adjusted.marginCreep >= 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400'
  const upliftColour =
    adjusted.pctIncrease >= 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400'
  const adjMarginPctDisplay = adjusted.adjustedMarginPct * 100

  return (
    <div className="border-2 border-amber-200 dark:border-amber-800 rounded-lg p-3 bg-amber-50/40 dark:bg-amber-950/30">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Adjusted GM (Contract Value)
          </span>
        </div>
        <span className={'text-xs font-semibold tabular-nums ' + marginColour(adjMarginPctDisplay)}>
          {adjMarginPctDisplay.toFixed(1)}% margin
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-amber-700/70 dark:text-amber-300/70 mb-0.5">Contract value</div>
          <div className="font-semibold tabular-nums text-amber-900 dark:text-amber-100">
            {formatCurrency(adjusted.adjustedTotal)}
          </div>
        </div>
        <div>
          <div className="text-amber-700/70 dark:text-amber-300/70 mb-0.5">Adjusted margin</div>
          <div className="font-semibold tabular-nums text-amber-900 dark:text-amber-100">
            {formatCurrency(adjusted.adjustedMargin)}
          </div>
        </div>
        <div>
          <div className="text-amber-700/70 dark:text-amber-300/70 mb-0.5">Margin creep</div>
          <div className={'font-semibold tabular-nums ' + creepColour}>
            {formatCurrency(adjusted.marginCreep)}
          </div>
        </div>
        <div>
          <div className="text-amber-700/70 dark:text-amber-300/70 mb-0.5">GM uplift vs raw</div>
          <div className={'font-semibold tabular-nums ' + upliftColour}>
            {(adjusted.pctIncrease * 100 >= 0 ? '+' : '') + (adjusted.pctIncrease * 100).toFixed(1)}pp
          </div>
        </div>
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="p-6 text-center">
      <Sparkles className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1">
        KPI Summary not available
      </h3>
      <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        This quote doesn't carry the Bravada KPI Summary header data
        (labour / products / plant / subcontractor revenue + budget
        breakdown). The card appears automatically on quotes loaded
        via the Bravada demo loader.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface KPISummaryPanelProps {
  quote: QuoteDetail | null
}

export default function KPISummaryPanel({ quote }: KPISummaryPanelProps) {
  const summary = parseKpiSummary(quote?.config)
  if (!summary) return <EmptyState />

  const totalCost = summary.rows.reduce((s, r) => s + r.cost, 0)
  const totalChargeout = summary.rows.reduce((s, r) => s + r.chargeout, 0)
  const totalMargin = totalChargeout - totalCost
  const totalMarginPct = marginPercent(totalCost, totalChargeout)

  return (
    <div className="p-4 space-y-4">
      {/* Title block */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-500" />
          KPI Summary
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Per-discipline cost vs charge-out from the source Excel
          KPI Summary tab. Margins are computed live.
        </p>
      </div>

      {/* Discipline breakdown */}
      <div className="space-y-2">
        {summary.rows.map(row => (
          <KPIRowItem key={row.label} row={row} totalChargeout={totalChargeout} />
        ))}
      </div>

      {/* Total margin card (RAW chargeout — costing model) */}
      <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50/40 dark:bg-blue-950/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sigma className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">Raw Total (Costing)</span>
          </div>
          <span className={'text-xs font-semibold tabular-nums ' + marginColour(totalMarginPct)}>
            {totalMarginPct.toFixed(1)}% margin
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="text-blue-700/70 dark:text-blue-300/70 mb-0.5">Cost</div>
            <div className="font-semibold tabular-nums text-blue-900 dark:text-blue-100">
              {formatCurrency(totalCost)}
            </div>
          </div>
          <div>
            <div className="text-blue-700/70 dark:text-blue-300/70 mb-0.5">Charge-out</div>
            <div className="font-semibold tabular-nums text-blue-900 dark:text-blue-100">
              {formatCurrency(totalChargeout)}
            </div>
          </div>
          <div>
            <div className="text-blue-700/70 dark:text-blue-300/70 mb-0.5">Margin</div>
            <div className="font-semibold tabular-nums text-blue-900 dark:text-blue-100">
              {formatCurrency(totalMargin)}
            </div>
          </div>
        </div>
      </div>

      {/* Adjusted GM (CONTRACT value — directors' decision number) */}
      {summary.adjusted && <AdjustedGmBlock adjusted={summary.adjusted} />}

      {/* Labour effort + Excel totals */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 mb-1">
            <Clock className="w-3.5 h-3.5" />
            Total labour effort
          </div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
            {summary.totalLabourHours.toLocaleString()} hrs
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
            ≈ {summary.totalLabourDays.toLocaleString()} days
          </div>
        </div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900">
          <div className="text-slate-500 dark:text-slate-400 mb-1">Quote (Excel)</div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 tabular-nums">
            {formatCurrency(summary.quoteTotalInclGst)} incl. GST
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
            {formatCurrency(summary.quoteRevenueExclGst)} ex GST
            {summary.discount !== 0 ? ` · −${formatCurrency(Math.abs(summary.discount))}` : ''}
          </div>
        </div>
      </div>
    </div>
  )
}
